import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { menuService, toppingsService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import Modal from '../components/Common/Modal';
import { getSizeOptions, priceForSize, hasSizeOptions } from '../utils/sizeOptions';

const DrinkDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const { formatPrice } = useCurrency();

  const [drink, setDrink] = useState(null);
  const [toppings, setToppings] = useState([]);
  const [selectedToppings, setSelectedToppings] = useState([]);
  const [selectedSize, setSelectedSize] = useState('large');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null
  });

  const showModal = (type, title, message, onConfirm = null) => {
    setModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm
    });
  };

  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    const fetchDrinkDetails = async () => {
      try {
        const response = await menuService.getItem(id);
        if (response.data && response.data.success) {
          setDrink(response.data.data);
        } else {
          throw new Error('Drink not found');
        }
      } catch (error) {
        console.error('Error fetching drink details:', error);
        setError('Failed to load drink details');
      } finally {
        setLoading(false);
      }
    };

    const fetchToppings = async () => {
      try {
        const response = await toppingsService.getAllToppings();
        if (response.data && response.data.success) {
          setToppings(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching toppings:', error);
      }
    };

    fetchDrinkDetails();
    fetchToppings();
  }, [id]);

  const handleToppingToggle = (topping) => {
    setSelectedToppings(prev => {
      const isSelected = prev.some(t => t.id === topping.id);
      if (isSelected) {
        return prev.filter(t => t.id !== topping.id);
      } else {
        return [...prev, topping];
      }
    });
  };

  const calculateTotal = () => {
    if (!drink) return 0;
    
    // Use priceForSize helper for size-based pricing
    const basePrice = priceForSize(drink, selectedSize);
    console.log('Price for size', selectedSize, ':', basePrice);
    const toppingsPrice = selectedToppings.reduce((sum, topping) => {
      return sum + (parseFloat(topping.price) || 0);
    }, 0);
    console.log('Toppings Price:', toppingsPrice);
    const total = (basePrice + toppingsPrice) * quantity;
    console.log('Total:', total);
    return total;
  };

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }

    const cartItem = {
      id: `${drink.id}-${Date.now()}`,
      drink: drink,
      size: selectedSize,
      toppings: selectedToppings,
      quantity: quantity,
      total: calculateTotal()
    };

    const success = addToCart(cartItem);
    if (success) {
      showModal('cart-success', 'Item Added! 🛒', 'Item added to cart successfully!');
    }
  };

  if (loading) {
    return (
      <div className="drink-detail-page">
        <div className="container">
          <div className="loading">Loading drink details...</div>
        </div>
      </div>
    );
  }

  if (error || !drink) {
    return (
      <div className="drink-detail-page">
        <div className="container">
          <div className="error">
            <h2>Drink not found</h2>
            <p>{error || 'The drink you\'re looking for doesn\'t exist.'}</p>
            <button onClick={() => navigate('/menu')} className="btn btn-primary">
              Back to Menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="drink-detail-page">
      <div className="container">
        <button onClick={() => navigate('/menu')} className="back-button">
          ← Back to Menu
        </button>

        <div className="drink-detail">
          <div className="drink-image">
            {drink.imageUrl && drink.imageUrl.trim() !== '' ? (
              <img 
                src={drink.imageUrl} 
                alt={drink.name}
                className="drink-photo"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className="image-placeholder" style={{display: (drink.imageUrl && drink.imageUrl.trim() !== '') ? 'none' : 'flex'}}>
              🚫📷
            </div>
          </div>

          <div className="drink-info">
            <h1>{drink.name}</h1>
            <p className="description">{drink.description}</p>
            <p className="category">Category: {drink.category}</p>
            <p className="base-price">
              {hasSizeOptions(drink) 
                ? `Price from: ${formatPrice(Math.min(drink.price, drink.sizes?.regular || drink.sizes?.small || drink.price))}`
                : `Base Price: ${formatPrice(drink.price)}`
              }
            </p>

            {isAuthenticated && (
              <div className="customization">
                {toppings.length > 0 && (
                  <div className="toppings-selection">
                    <h3>Toppings</h3>
                    <div className="toppings-grid">
                      {toppings.map(topping => (
                        <label key={topping.id} className="topping-option">
                          <input
                            type="checkbox"
                            checked={selectedToppings.some(t => t.id === topping.id)}
                            onChange={() => handleToppingToggle(topping)}
                          />
                          <span className="topping-label">
                            {topping.name} (+{formatPrice(topping.price)})
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Size Selection - only for cold beverages */}
                {hasSizeOptions(drink) && (
                  <div className="size-selection">
                    <h3>Size</h3>
                    <div className="size-options">
                      {getSizeOptions(drink).map(option => (
                        <button
                          key={option.value}
                          className={`size-btn ${selectedSize === option.value ? 'active' : ''} ${option.disabled ? 'disabled' : ''}`}
                          onClick={() => !option.disabled && setSelectedSize(option.value)}
                          disabled={option.disabled}
                          title={option.tooltip || ''}
                        >
                          <span className="size-label">{option.label}</span>
                          <span className="size-price">
                            {option.disabled ? 'Coming soon' : formatPrice(option.price)}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="quantity-selection">
                  <h3>Quantity</h3>
                  <div className="quantity-controls">
                    <button 
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="quantity-btn"
                      disabled={quantity <= 1}
                    >
                      -
                    </button>
                    <span className="quantity-display">{quantity}</span>
                    <button 
                      onClick={() => setQuantity(Math.min(10, quantity + 1))}
                      className="quantity-btn"
                      disabled={quantity >= 10}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="order-summary">
                  <div className="total-price">
                    <h3>Total: {formatPrice(calculateTotal())}</h3>
                  </div>
                  <button onClick={handleAddToCart} className="btn btn-primary btn-large">
                    Add to Cart
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={modal.isOpen}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onConfirm={modal.onConfirm}
        onClose={closeModal}
        onGoToCart={() => navigate('/cart')}
        onContinueShopping={() => navigate('/menu')}
      />

      <style jsx>{`
        .drink-detail-page {
          min-height: 100vh;
          background-color: #fff8f0;
          padding: 40px 0 80px;
        }
        
        .back-button {
          background: none;
          border: none;
          color: #C2185B;
          font-size: 1rem;
          cursor: pointer;
          margin-bottom: 30px;
          padding: 10px 0;
        }
        
        .back-button:hover {
          text-decoration: underline;
        }
        
        .loading,
        .error {
          text-align: center;
          padding: 100px 20px;
        }
        
        .error h2 {
          color: #ef4444;
          margin-bottom: 15px;
        }
        
        .drink-detail {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 60px;
          background-color: white;
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }
        
        .drink-image {
          text-align: center;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f8f9fa;
          border-radius: 15px;
          overflow: hidden;
          height: 400px;
        }
        
        .drink-photo {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 15px;
        }
        
        .image-placeholder {
          font-size: 6rem;
          color: #ccc;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          width: 100%;
          border: 2px dashed #e0e0e0;
          border-radius: 15px;
          background-color: #f9f9f9;
        }
        
        .image-placeholder::after {
          content: "No Image Available";
          font-size: 1rem;
          color: #999;
          margin-top: 15px;
        }
        
        .drink-info h1 {
          color: #C2185B;
          font-size: 2.5rem;
          margin-bottom: 20px;
        }
        
        .description {
          color: #666;
          font-size: 1.1rem;
          line-height: 1.6;
          margin-bottom: 15px;
        }
        
        .category,
        .base-price {
          color: #666;
          margin-bottom: 10px;
        }
        
        .base-price {
          font-weight: bold;
          font-size: 1.2rem;
          color: #C2185B;
        }
        
        .auth-notice {
          background-color: #fcf0ff;
          border: 2px solid #C2185B;
          border-radius: 10px;
          padding: 20px;
          margin: 30px 0;
          text-align: center;
        }
        
        .customization {
          margin-top: 30px;
        }
        
        .customization h3 {
          color: #C2185B;
          margin-bottom: 15px;
          font-size: 1.3rem;
        }
        
        .size-selection {
          margin-bottom: 20px;
        }
        
        .size-options {
          display: flex;
          gap: 15px;
          margin-bottom: 30px;
        }
        
        .size-btn {
          flex: 1;
          padding: 15px;
          border: 2px solid #C2185B;
          background-color: white;
          border-radius: 12px;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 5px;
          transition: all 0.3s ease;
        }
        
        .size-btn:hover:not(:disabled) {
          background-color: #fcf0ff;
        }
        
        .size-btn.active {
          background-color: #C2185B;
          color: white;
        }
        
        .size-btn.disabled {
          opacity: 0.5;
          cursor: not-allowed;
          border-style: dashed;
        }
        
        .size-label {
          font-weight: bold;
          font-size: 1.1rem;
        }
        
        .size-price {
          font-size: 0.9rem;
          opacity: 0.9;
        }
        
        .size-option {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }
        
        .size-option input[type="radio"] {
          margin: 0;
        }
        
        .toppings-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
          margin-bottom: 30px;
        }
        
        .topping-option {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          padding: 10px;
          border-radius: 8px;
          transition: background-color 0.3s ease;
        }
        
        .topping-option:hover {
          background-color: #fcf0ff;
        }
        
        .topping-option input[type="checkbox"] {
          margin: 0;
        }
        
        .quantity-controls {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-bottom: 30px;
        }
        
        .quantity-btn {
          width: 40px;
          height: 40px;
          border: 2px solid #C2185B;
          background-color: white;
          color: #C2185B;
          border-radius: 50%;
          cursor: pointer;
          font-size: 1.2rem;
          font-weight: bold;
          transition: all 0.3s ease;
        }
        
        .quantity-btn:hover:not(:disabled) {
          background-color: #C2185B;
          color: white;
        }
        
        .quantity-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .quantity-display {
          font-size: 1.2rem;
          font-weight: bold;
          min-width: 30px;
          text-align: center;
        }
        
        .order-summary {
          border-top: 2px solid #e5e5e5;
          padding-top: 30px;
        }
        
        .total-price {
          margin-bottom: 20px;
        }
        
        .total-price h3 {
          color: #C2185B;
          font-size: 1.8rem;
        }
        
        .btn-large {
          padding: 15px 40px;
          font-size: 1.1rem;
          width: 100%;
        }
        
        @media (max-width: 768px) {
          .drink-detail {
            grid-template-columns: 1fr;
            gap: 30px;
            padding: 25px;
          }
          
          .drink-info h1 {
            font-size: 2rem;
          }
          
          .drink-image {
            height: 250px;
          }
          
          .image-placeholder {
            font-size: 4rem;
          }
          
          .image-placeholder::after {
            font-size: 0.8rem;
          }
          
          .size-options {
            flex-direction: column;
          }
          
          .toppings-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default DrinkDetailPage; 